import { batchScheduler, WriteBatchScheduler } from 'batch-promise';
import { CACHE_STORE } from './connection';
import { parseTypeName } from '../resources/resource';
import { Metrics } from '../metrics/metrics';

type WriteInput = {
  key: string;
  data: unknown;
  storedAt: number;
};

export type PersistenceWriter = {
  write: (key: string, data: unknown, storedAt?: number) => Promise<undefined>;
  flush: () => Promise<void>;
};

export const createPersistenceWriter = (
  connection: Promise<IDBDatabase>
): PersistenceWriter => {
  const scheduler: WriteBatchScheduler<WriteInput, undefined, string> =
    batchScheduler()
      .write<WriteInput>()
      .identity(({ key }) => key)
      .partition(() => CACHE_STORE)
      .execute(async (calls) => {
        const list = Object.values(calls);
        const db = await connection;

        return new Promise<void>((resolve, reject) => {
          try {
            const tx = db.transaction(CACHE_STORE, 'readwrite');
            const store = tx.objectStore(CACHE_STORE);

            for (const { key, data, storedAt } of list) {
              const stringifyStart = performance.now();
              const serialized = JSON.stringify(data);
              Metrics.timer('persistence.stringify').update(
                performance.now() - stringifyStart
              );

              store.put(
                {
                  data: serialized,
                  storedAt,
                  typeName: parseTypeName(key),
                },
                key
              );
            }

            const writeStart = performance.now();
            tx.oncomplete = () => {
              Metrics.timer('persistence.idbWrite').update(
                performance.now() - writeStart
              );
              resolve();
            };
            tx.onerror = reject;
            tx.onabort = () => reject(tx.error ?? Error('Transaction aborted'));
          } catch (e) {
            reject(e);
          }
        });
      })
      .extract(() => undefined)
      .debounce(3000, 10000);

  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void scheduler.flush();
      }
    });

    document.addEventListener('pagehide', () => {
      void scheduler.flush();
    });
  } catch {
    // SSR / test environment without document
  }

  return {
    write: (key, data, storedAt = Date.now()) =>
      scheduler.queue({ key, data, storedAt }),
    flush: () => scheduler.flush(),
  };
};
